/**
 * Input Component
 *
 * A styled input component with label, validation states, and helper text.
 */

import { forwardRef, useId } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/utils/cn';

// ============================================
// Input Variants
// ============================================

const inputVariants = cva(
  [
    'flex w-full rounded-md border bg-background px-3 py-2',
    'text-sm text-foreground',
    'transition-colors duration-200',
    'placeholder:text-foreground-muted',
    'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-0',
    'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-secondary-50',
    'dark:bg-surface-dark dark:text-foreground-dark dark:placeholder:text-foreground-dark-muted',
  ],
  {
    variants: {
      variant: {
        default: [
          'border-border',
          'focus:border-primary',
          'dark:border-border-dark',
        ],
        error: [
          'border-danger',
          'focus:border-danger focus:ring-danger',
          'dark:border-danger',
        ],
        success: [
          'border-success',
          'focus:border-success focus:ring-success',
        ],
      },
      inputSize: {
        sm: 'h-8 text-xs',
        md: 'h-9',
        lg: 'h-10 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      inputSize: 'md',
    },
  }
);

// ============================================
// Input Props
// ============================================

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  /** Label text */
  label?: string;
  /** Helper text shown below input */
  helperText?: string;
  /** Error message (overrides helperText when present) */
  error?: string;
  /** Success message */
  success?: string;
  /** Left icon/element */
  leftElement?: React.ReactNode;
  /** Right icon/element */
  rightElement?: React.ReactNode;
  /** Container className */
  containerClassName?: string;
  /** Whether field is required (adds asterisk to label) */
  required?: boolean;
}

// ============================================
// Input Component
// ============================================

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      containerClassName,
      variant,
      inputSize,
      label,
      helperText,
      error,
      success,
      leftElement,
      rightElement,
      required,
      id: providedId,
      disabled,
      ...props
    },
    ref
  ) => {
    // Generate unique ID if not provided
    const generatedId = useId();
    const id = providedId || generatedId;

    // Determine variant based on error/success state
    const computedVariant = error ? 'error' : success ? 'success' : variant;

    // Determine helper text to show
    const displayText = error || success || helperText;
    const textColor = error ? 'text-danger' : success ? 'text-success' : 'text-foreground-muted';

    return (
      <div className={cn('space-y-1.5', containerClassName)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={id}
            className={cn(
              'block text-sm font-medium text-foreground',
              'dark:text-foreground-dark',
              disabled && 'opacity-50'
            )}
          >
            {label}
            {required && <span className="ml-0.5 text-danger">*</span>}
          </label>
        )}

        {/* Input wrapper */}
        <div className="relative">
          {/* Left element */}
          {leftElement && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-foreground-muted">
              {leftElement}
            </div>
          )}

          {/* Input */}
          <input
            id={id}
            ref={ref}
            disabled={disabled}
            className={cn(
              inputVariants({ variant: computedVariant, inputSize }),
              leftElement && 'pl-10',
              rightElement && 'pr-10',
              className
            )}
            {...props}
          />

          {/* Right element */}
          {rightElement && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-foreground-muted">
              {rightElement}
            </div>
          )}
        </div>

        {/* Helper/Error text */}
        {displayText && (
          <p className={cn('text-xs', textColor, 'dark:opacity-90')}>{displayText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

// Export variants for external use
export { inputVariants };
